<template>
    <section class="company-users">
        <div class="users-header">
            <h4>Users</h4>
            <button
                v-if="!showForm"
                class="btn-add-user"
                @click="showForm = true"
            >
                <i class="fa fa-plus"></i> Add User
            </button>
        </div>

        <div v-if="showForm" class="add-user-form">
            <h5>Add New User</h5>
            <div class="form-row">
                <div class="form-group">
                    <label>First Name</label>
                    <input v-model="form.first_name" type="text" placeholder="First name" />
                </div>
                <div class="form-group">
                    <label>Last Name</label>
                    <input v-model="form.last_name" type="text" placeholder="Last name" />
                </div>
                <div class="form-group">
                    <label>Email</label>
                    <input v-model="form.email" type="email" placeholder="Email address" />
                </div>
            </div>
            <div class="form-actions">
                <button class="btn-submit" :disabled="creating" @click="createUser">
                    <i v-if="creating" class="fa fa-spinner fa-spin"></i>
                    {{ creating ? 'Creating...' : 'Create User' }}
                </button>
                <button class="btn-cancel" @click="resetForm">Cancel</button>
            </div>
        </div>

        <div v-if="loading" class="loading-state">
            <i class="fa fa-spinner fa-spin"></i> Loading users...
        </div>

        <table v-else-if="users.length" class="users-table">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="user in users" :key="user.id" :class="{ 'deactivated-row': user.deactivated }">
                    <td class="user-name">{{ user.first_name }} {{ user.last_name }}</td>
                    <td>{{ user.email }}</td>
                    <td>
                        <span v-if="user.deactivated" class="status-badge status-deactivated">Deactivated</span>
                        <span v-else class="status-badge status-active">Active</span>
                    </td>
                    <td>
                        <button
                            v-if="user.deactivated"
                            class="btn-action btn-reactivate"
                            :disabled="toggling === user.id"
                            @click="toggleActivation(user.id, 'activate')"
                        >
                            <i class="fa fa-toggle-on"></i> Reactivate
                        </button>
                        <button
                            v-else
                            class="btn-action btn-deactivate"
                            :disabled="toggling === user.id"
                            @click="toggleActivation(user.id, 'deactivate')"
                        >
                            <i class="fa fa-toggle-off"></i> Deactivate
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>

        <p v-else class="empty-state">No users found for this company.</p>
    </section>
</template>

<script>
import { useLocalized } from '../../composables/useLocalized';

export default {
    props: {
        companyId: {
            type: [Number, String],
            required: true
        }
    },

    data() {
        return {
            users: [],
            loading: false,
            creating: false,
            toggling: null,
            showForm: false,
            form: {
                first_name: '',
                last_name: '',
                email: ''
            }
        };
    },

    setup() {
        const { restUrl, restNonce } = useLocalized();
        return { restUrl, restNonce };
    },

    watch: {
        companyId: {
            immediate: true,
            handler() {
                this.loadUsers();
            }
        }
    },

    methods: {
        async loadUsers() {
            this.loading = true;
            try {
                const response = await fetch(`${this.restUrl}/company/${this.companyId}/users`, {
                    headers: { 'X-WP-Nonce': this.restNonce }
                });
                const result = await response.json();
                if (result.success) {
                    this.users = result.data;
                } else {
                    toastr.error(result.message || 'Failed to load users', 'Error');
                }
            } catch (error) {
                toastr.error('Failed to load users', 'Error');
            } finally {
                this.loading = false;
            }
        },

        async createUser() {
            if (!this.form.email || !this.form.first_name || !this.form.last_name) {
                toastr.warning('Please fill in all fields.', 'Warning');
                return;
            }

            this.creating = true;
            try {
                const response = await fetch(`${this.restUrl}/company/create-user`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': this.restNonce
                    },
                    body: JSON.stringify({
                        company_id: this.companyId,
                        email: this.form.email,
                        first_name: this.form.first_name,
                        last_name: this.form.last_name
                    })
                });
                const result = await response.json();

                if (result.success) {
                    this.users.push(result.data);
                    this.resetForm();
                    toastr.success('User created. A password setup email has been sent.', 'Success');
                } else {
                    toastr.error(result.message || 'Failed to create user', 'Error');
                }
            } catch (error) {
                toastr.error('Failed to create user', 'Error');
            } finally {
                this.creating = false;
            }
        },

        async toggleActivation(userId, action) {
            this.toggling = userId;
            try {
                const response = await fetch(`${this.restUrl}/company/toggle-user-activation`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': this.restNonce
                    },
                    body: JSON.stringify({ user_id: userId, action })
                });
                const result = await response.json();

                if (result.success) {
                    const user = this.users.find(u => u.id === userId);
                    if (user) {
                        user.deactivated = action === 'deactivate';
                        user.role = action === 'deactivate' ? '' : 'client';
                    }
                    toastr.success(result.message, 'Success');
                } else {
                    toastr.error(result.message || 'Failed to update user', 'Error');
                }
            } catch (error) {
                toastr.error('Failed to update user', 'Error');
            } finally {
                this.toggling = null;
            }
        },

        resetForm() {
            this.showForm = false;
            this.form = { first_name: '', last_name: '', email: '' };
        }
    }
};
</script>
